import { Chart, useChart } from '@chakra-ui/charts';
import { Box } from '@chakra-ui/react';
import { useId, useMemo } from 'react';
import { Area, AreaChart, Bar, BarChart, Tooltip } from 'recharts';

interface SparklineProps {
  data: { value: number }[];
  type?: 'area' | 'bar';
  color?: string;
  /** Label for tooltip values (e.g. "conversions") */
  tooltipLabel?: string;
  /**
   * Inline mode fills the parent box instead of floating in a card's bottom-right
   * corner. Use it when the sparkline IS the content (a table cell, a detail
   * curve), not an accent overlaid on a metric card. The parent must size it.
   */
  inline?: boolean;
  /**
   * Fill opacity at the top and the bottom of the shape. Defaults keep the
   * historical look (area 0.2 → 0.04, bars 0.55 → 0.15); `[1, 1]` gives solid bars.
   */
  fillOpacity?: [top: number, bottom: number];
}

const DEFAULT_FILL_OPACITY: Record<'area' | 'bar', [number, number]> = {
  area: [0.2, 0.04],
  bar: [0.55, 0.15],
};

export const Sparkline: React.FC<SparklineProps> = ({
  data,
  type = 'area',
  color = 'blue.500',
  tooltipLabel,
  inline = false,
  fillOpacity,
}) => {
  // One gradient per instance: a shared id let two sparklines of different colours
  // paint each other's fill.
  const gradientId = `sparkline-${type}-${useId().replace(/:/g, '')}`;
  const series = useMemo(() => [{ name: 'value' as const, color }], [color]);
  const chart = useChart({ data, series });

  if (data.length === 0) return null;

  const showTooltip = type === 'bar' && tooltipLabel;
  const [topOpacity, bottomOpacity] = fillOpacity ?? DEFAULT_FILL_OPACITY[type];

  const layout = inline
    ? ({ position: 'relative', width: 'full', height: 'full' } as const)
    : ({
        position: 'absolute',
        bottom: '8px',
        right: '10px',
        width: '45%',
        height: '50%',
      } as const);

  return (
    <Box
      data-testid="sparkline"
      data-sparkline-type={type}
      {...layout}
      pointerEvents={showTooltip ? 'auto' : 'none'}
      overflow="visible"
      zIndex={showTooltip ? 2 : 0}
    >
      <Chart.Root width="full" height="full" chart={chart}>
        {type === 'area' ? (
          <AreaChart data={chart.data} margin={{ top: 6, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <Chart.Gradient
                id={gradientId}
                stops={[
                  { offset: '0%', color, opacity: topOpacity },
                  { offset: '100%', color, opacity: bottomOpacity },
                ]}
              />
            </defs>
            <Area
              type="monotone"
              dataKey={chart.key('value')}
              stroke={chart.color(color)}
              strokeWidth={1.5}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
            />
          </AreaChart>
        ) : (
          <BarChart data={chart.data} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
            <defs>
              <Chart.Gradient
                id={gradientId}
                stops={[
                  { offset: '0%', color, opacity: topOpacity },
                  { offset: '100%', color, opacity: bottomOpacity },
                ]}
              />
            </defs>
            {showTooltip && (
              <Tooltip
                cursor={false}
                animationDuration={100}
                offset={0}
                position={{ y: -30 }}
                content={<Chart.Tooltip />}
              />
            )}
            <Bar
              dataKey={chart.key('value')}
              fill={`url(#${gradientId})`}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        )}
      </Chart.Root>
    </Box>
  );
};
